import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import useAxios from "../../../hooks/useAxios";
import AnswerRow from "../ActiveSessionsPage/AnswerRow";
import Header from "../../../components/Header/Header";
import StyledSelect from "../../../components/common/StyledComponents/StyledSelect";
import {
  MenuItem,
  Box,
  Typography,
  Paper,
  IconButton,
  TextField,
  InputAdornment,
  Button,
  Collapse,
  Tooltip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import "./AttemptedUsersPage.css";
import { useLocation } from "react-router-dom";
import ArticleIcon from "@mui/icons-material/Article";
import ReportModal from "../../../components/Reporting/Modals/ReportModal/ReportModal";

const navLinks = [];

function AttemptedUsersPage() {
  const location = useLocation();
  const enableClassFilter = location.state?.enableClassFilter || false;
  const axiosInstance = useAxios();
  const [quizzes, setQuizzes] = useState([]);
  const [classes, setClasses] = useState([]);
  const [filteredQuizzes, setFilteredQuizzes] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedQuiz, setSelectedQuiz] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [currentReport, setCurrentReport] = useState(null);

  // Filters
  const [studentNameFilter, setStudentNameFilter] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [expandedSessionId, setExpandedSessionId] = useState(null);

  const fetchQuizzesAndClasses = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get("/quizzes/owner");
      if (response.data.success) {
        const quizzes = response.data.quizzes;
        // Extract classes from both legacy class and attached_classes
        const classes = [];

        // First, get classes from legacy class relationship
        const legacyClasses = quizzes
          .filter((quiz) => quiz.class)
          .map((quiz) => quiz.class);

        // Then, get classes from attached_classes (new system)
        const attachedClasses = quizzes
          .filter(
            (quiz) => quiz.attached_classes && quiz.attached_classes.length > 0
          )
          .flatMap((quiz) => quiz.attached_classes);

        // Combine and deduplicate
        const allClasses = [...legacyClasses, ...attachedClasses];
        const uniqueClasses = allClasses.filter(
          (value, index, self) =>
            self.findIndex((c) => c.id === value.id) === index
        );

        classes.push(...uniqueClasses);

        setQuizzes(quizzes);
        setClasses(classes);
        setFilteredQuizzes(quizzes);
      } else {
        toast.error(response.data.message || "Failed to fetch quizzes.");
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message || "An unexpected error occurred."
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchReport = async (quizId) => {
    if (!quizId) return;
    setLoading(true);
    setError(null);

    try {
      const params = {};
      if (studentNameFilter) params.student_name = studentNameFilter;
      if (startDate) params.start_date = startDate.toISOString().split("T")[0];
      if (endDate) params.end_date = endDate.toISOString().split("T")[0];

      const response = await axiosInstance.get(
        `/reporting/${quizId}/attempted-users`,
        {
          params,
        }
      );

      if (response.data.success) {
        setSessions(response.data.sessions);
      } else {
        toast.error(response.data.message || "Failed to fetch sessions.");
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message || "An unexpected error occurred."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuizzesAndClasses();
  }, []);

  useEffect(() => {
    if (selectedQuiz) {
      fetchReport(selectedQuiz);
    }
  }, [selectedQuiz, studentNameFilter, startDate, endDate]);

  const handleSelectQuiz = (event) => {
    const quizId = event.target.value;
    setSelectedQuiz(quizId);
    fetchReport(quizId);
  };

  const handleSelectClass = (event) => {
    const classId = event.target.value;
    setSelectedClass(classId);

    if (classId) {
      const filtered = quizzes.filter((quiz) => {
        // Check legacy class relationship
        if (quiz.class?.id === classId) {
          return true;
        }
        // Check attached classes (new system)
        if (
          quiz.attached_classes &&
          quiz.attached_classes.some((cls) => cls.id === classId)
        ) {
          return true;
        }
        return false;
      });
      setFilteredQuizzes(filtered);
      setSelectedQuiz("");
    } else {
      setFilteredQuizzes(quizzes);
    }
  };

  const handleReload = () => {
    if (selectedQuiz) {
      fetchReport(selectedQuiz);
    }
  };

  const clearFilters = () => {
    setStudentNameFilter("");
    setStartDate(null);
    setEndDate(null);
  };

  const toggleExpand = (sessionId) => {
    setExpandedSessionId((prev) => (prev === sessionId ? null : sessionId));
  };

  const getUserDisplayName = (user) => {
    if (user.role === "student" && user.student_profile) {
      return user.student_profile.full_name;
    } else if (user.family_profile) {
      return user.family_profile.family_name;
    }
    return user.username;
  };

  const handleViewReportClick = async (sessionId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosInstance.get(`/sessions/${sessionId}/report`);
      if (response.data.success) {
        setCurrentReport(response.data.report);
        setReportModalOpen(true);
      } else {
        toast.error(response.data.message || "Failed to fetch report.");
      }
    } catch (err) {
      console.error("Error fetching session report:", err);
      toast.error(
        err.response?.data?.message || "An unexpected error occurred."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header navLinks={navLinks} isScrollEffect={false} />
      <div className="attempted-users-page page-content">
        {/* Page Title and Controls */}
        <div className="title-container">
          <Typography variant="h4" className="page-title">
            Attempted Users Report
          </Typography>
          <div className="controls">
            {enableClassFilter && (
              <StyledSelect
                className="class-select"
                value={selectedClass || ""}
                onChange={handleSelectClass}
                displayEmpty
                inputProps={{ "aria-label": "Select a class" }}
              >
                <MenuItem value="">All Classes</MenuItem>
                {classes.map((classItem) => (
                  <MenuItem value={classItem.id} key={classItem.id}>
                    {classItem.class_name}
                  </MenuItem>
                ))}
              </StyledSelect>
            )}
            <StyledSelect
              className="quiz-select"
              value={selectedQuiz || ""}
              onChange={handleSelectQuiz}
              displayEmpty
              inputProps={{ "aria-label": "Select an Assessment" }}
            >
              <MenuItem value="" disabled>
                Select an Assessment
              </MenuItem>
              {filteredQuizzes.map((quiz) => (
                <MenuItem value={quiz.id} key={quiz.id}>
                  {quiz.title}
                </MenuItem>
              ))}
            </StyledSelect>

            <IconButton onClick={handleReload} className="primary-button">
              <RefreshIcon />
            </IconButton>
          </div>
        </div>

        {/* Filter Section */}
        <Paper className="filter-section" elevation={2}>
          <TextField
            variant="outlined"
            placeholder="Filter by Student Name"
            value={studentNameFilter}
            onChange={(e) => setStudentNameFilter(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            className="filter-input"
          />
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              renderInput={(params) => (
                <TextField {...params} className="date-picker" />
              )}
            />
            <DatePicker
              label="End Date"
              value={endDate}
              minDate={startDate}
              onChange={(newValue) => setEndDate(newValue)}
              renderInput={(params) => (
                <TextField {...params} className="date-picker" />
              )}
            />
          </LocalizationProvider>
          <div className="filters-actions">
            <Button
              onClick={() => clearFilters()}
              className="secondary-button apply-filters-btn"
              id="small-button"
            >
              Clear Filters
            </Button>
            <Button
              onClick={() => fetchReport(selectedQuiz)}
              className="primary-button apply-filters-btn"
              id="small-button"
            >
              Apply Filters
            </Button>
          </div>
        </Paper>

        {loading && (
          <div className="loading-state">
            <div className="spinner"></div>
            <Typography>Loading attempted users report...</Typography>
          </div>
        )}

        {error && (
          <div className="error-state">
            <Typography color="error">{error}</Typography>
          </div>
        )}

        {!loading && !error && selectedQuiz && sessions.length === 0 && (
          <div className="no-data-state">
            <Typography>
              No completed sessions found for this Assessment.
            </Typography>
          </div>
        )}

        {!loading && !error && selectedQuiz && sessions.length > 0 && (
          <Paper elevation={1}>
            <div className="table-container">
              <table className="custom-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Role</th>
                    <th>Attempt</th>
                    <th>Completion Date</th>
                    <th>Score / Total</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((session) => {
                    const user = session.user;
                    const name = getUserDisplayName(user);

                    const totalTasks = session.quiz.questions.reduce(
                      (count, q) => {
                        return (
                          count +
                          1 +
                          (q.subquestions ? q.subquestions.length : 0)
                        );
                      },
                      0
                    );

                    const correctCount = session.user_answers.filter(
                      (a) => a.evaluation === 1
                    ).length;
                    const completionDate = session.updated_at
                      ? new Date(session.updated_at).toLocaleString()
                      : "N/A";

                    const expanded = expandedSessionId === session.id;

                    if (user.role === "student") {
                      return (
                        <React.Fragment key={session.id}>
                          <tr className="data-row">
                            <td>{name}</td>
                            <td style={{ textTransform: "capitalize" }}>
                              {user.role}
                            </td>
                            <td>{session.attempt}</td>
                            <td>{completionDate}</td>
                            <td>
                              {correctCount}/{totalTasks} correct
                            </td>
                            <td>
                              <Tooltip title={"Details"}>
                                <IconButton
                                  onClick={() => toggleExpand(session.id)}
                                >
                                  {expanded ? (
                                    <ExpandLessIcon />
                                  ) : (
                                    <ExpandMoreIcon />
                                  )}
                                </IconButton>
                              </Tooltip>

                              <Tooltip title={"AI Report"}>
                                <IconButton
                                  onClick={() =>
                                    handleViewReportClick(session.id)
                                  }
                                >
                                  <ArticleIcon />
                                </IconButton>
                              </Tooltip>
                            </td>
                          </tr>

                          {expanded && (
                            <tr className="expanded-row">
                              <td colSpan={6} className="collapse-container">
                                <Collapse
                                  in={expanded}
                                  timeout="auto"
                                  unmountOnExit
                                >
                                  <Box className="answers-container">
                                    {Object.entries(
                                      session.grouped_answers || {}
                                    ).map(([questionId, group]) => {
                                      const {
                                        main_answer,
                                        subanswers,
                                        rollup_answer,
                                      } = group;

                                      // If there's a real main-answer (not rollup), show it
                                      if (
                                        main_answer &&
                                        !main_answer.is_rollup
                                      ) {
                                        return (
                                          <AnswerRow
                                            key={main_answer.id}
                                            answer={main_answer}
                                          />
                                        );
                                      }

                                      // If no main_answer but subanswers exist => show a top-level "header card"
                                      if (
                                        !main_answer &&
                                        subanswers.length > 0
                                      ) {
                                        const questionBody =
                                          subanswers[0].question?.body ||
                                          "No question text";

                                        return (
                                          <React.Fragment key={questionId}>
                                            {/* main question "header" */}
                                            <div className="answer-row main-question-card">
                                              <div className="answer-main">
                                                <span className="question-body">
                                                  {questionBody}
                                                </span>
                                              </div>
                                            </div>

                                            {/* subquestions */}
                                            {subanswers.map((sub) => (
                                              <AnswerRow
                                                key={sub.id}
                                                answer={sub}
                                              />
                                            ))}
                                          </React.Fragment>
                                        );
                                      }

                                      // Optionally handle the roll-up if you want to show it
                                      // e.g. <div>{rollup_answer.grade}</div> etc.

                                      return null;
                                    })}
                                  </Box>
                                </Collapse>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    }
                  })}
                </tbody>
              </table>
            </div>
          </Paper>
        )}
      </div>
      <ReportModal
        open={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        report={currentReport}
      />
      <ToastContainer position="top-right" autoClose={5000} />
    </>
  );
}

export default AttemptedUsersPage;
